import { useEffect, useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Loader2, RotateCcw, Save, MessageSquare, Info } from 'lucide-react';
import { TopBanner } from '@/components/TopBanner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  useBotMessages,
  useUpdateBotMessage,
  useResetBotMessage,
} from '@/hooks/useBotMessages';
import type { BotMessage } from '@/api/bot-messages.api';

export const Route = createFileRoute('/_authenticated/mensajes')({
  component: MensajesPage,
});

const CATEGORY_ORDER = ['Bienvenida', 'Carga', 'Retiro', 'Soporte', 'Cuponera', 'General', 'Error'];

const CATEGORY_VARIANT: Record<string, 'default' | 'secondary' | 'success' | 'warning' | 'destructive'> = {
  Bienvenida: 'success',
  Carga: 'default',
  Retiro: 'warning',
  Soporte: 'secondary',
  Cuponera: 'success',
  General: 'secondary',
  Error: 'destructive',
};

function MensajesPage() {
  const { data: messages = [], isLoading } = useBotMessages();

  const grouped = useMemo(() => {
    const by: Record<string, BotMessage[]> = {};
    for (const m of messages) (by[m.category] ??= []).push(m);
    return CATEGORY_ORDER.filter((c) => by[c]?.length).map((c) => ({ category: c, items: by[c] }));
  }, [messages]);

  const overriddenCount = messages.filter((m) => m.isOverridden).length;

  return (
    <>
      <TopBanner
        title="Mensajes del bot"
        subtitle="Editá los textos que el bot le envía al cliente — sin tocar código"
      />

      <div className="flex-1 overflow-auto p-4 sm:p-6">
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            Estos son los mensajes automáticos del bot del chat. Usá variables como{' '}
            <code className="rounded bg-blue-100 px-1">{'{{username}}'}</code> y se reemplazan solas.
            {overriddenCount > 0 && (
              <> Tenés <strong>{overriddenCount}</strong> mensaje(s) personalizado(s).</>
            )}
          </div>
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Cargando mensajes…
          </div>
        )}

        {!isLoading &&
          grouped.map(({ category, items }) => (
            <div key={category} className="mb-6">
              <div className="mb-2 flex items-center gap-2">
                <Badge variant={CATEGORY_VARIANT[category] || 'secondary'}>{category}</Badge>
                <span className="text-xs text-muted-foreground">{items.length} mensaje(s)</span>
              </div>
              <div className="grid gap-3">
                {items.map((m) => (
                  <BotMessageCard key={m.key} message={m} />
                ))}
              </div>
            </div>
          ))}
      </div>
    </>
  );
}

function BotMessageCard({ message }: { message: BotMessage }) {
  const [text, setText] = useState(message.body);
  const updateMutation = useUpdateBotMessage();
  const resetMutation = useResetBotMessage();

  // Re-sincronizar cuando cambia el body desde el server (guardado/reset).
  useEffect(() => {
    setText(message.body);
  }, [message.body]);

  const dirty = text.trim() !== message.body.trim();
  const empty = !text.trim();
  const busy = updateMutation.isPending || resetMutation.isPending;

  return (
    <Card>
      <CardContent className="p-4">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-semibold">{message.label}</span>
            {message.isOverridden && (
              <Badge variant="warning" className="text-[10px]">Personalizado</Badge>
            )}
          </div>
          <code className="text-[11px] text-muted-foreground">{message.key}</code>
        </div>

        <textarea
          className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={busy}
        />

        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-1">
            {message.variables.length > 0 ? (
              message.variables.map((v) => (
                <button
                  key={v}
                  type="button"
                  className="rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground hover:bg-muted/70"
                  title="Insertar variable"
                  onClick={() => setText((t) => `${t}{{${v}}}`)}
                >
                  {`{{${v}}}`}
                </button>
              ))
            ) : (
              <span className="text-[11px] text-muted-foreground">Sin variables</span>
            )}
          </div>

          <div className="flex items-center gap-2">
            {message.isOverridden && (
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() => resetMutation.mutate(message.key)}
                title="Volver al texto original"
              >
                {resetMutation.isPending ? (
                  <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <RotateCcw className="mr-1 h-3.5 w-3.5" />
                )}
                Restaurar
              </Button>
            )}
            <Button
              size="sm"
              disabled={busy || !dirty || empty}
              onClick={() => updateMutation.mutate({ key: message.key, body: text })}
            >
              {updateMutation.isPending ? (
                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Save className="mr-1 h-3.5 w-3.5" />
              )}
              Guardar
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
